// src/components/PurchaseHistory.js
import React, { useEffect, useState, useCallback, useContext } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  onSnapshot,
  query,
  orderBy
} from "firebase/firestore";
import { db, logout } from "../utils/firebase";
import Spinner from "./Spinner";
import "./PurchaseHistory.css";
import TokenHistory from "./TokenHistory.js"; // keep your existing import
import { TokenContext } from "./tokenContext"; // import TokenContext
import GlobalNavDrawer from "../components/GlobalNavDrawer";



export default function PurchaseHistory() {
  const [user, setUser] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { balance } = useContext(TokenContext);
 
  // Auth + receipts stream
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (u) {
        const colRef = collection(db, "users", u.uid, "purchaseReceipts");
        const q = query(colRef, orderBy("createdAt", "desc"));
        const unsubReceipts = onSnapshot(q, (snapshot) => {
          const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          setReceipts(rows);
        });

        return () => {
          unsubReceipts();
        };
      } else {
        setReceipts([]);
      }
    });
    return () => unsub();
  }, []);

  const openStripeReceipt = (url) => {
    if (!url) return alert("No Stripe receipt URL available.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyText = useCallback((txt) => {
    navigator.clipboard?.writeText(String(txt)).then(() => {
      // Tiny toast replacement; keep your alert style for now
      alert("Copied to clipboard");
    });
  }, []);

    if (user === null || receipts === null) {
    return (
      <div className="PH-Menu-container">
        <h1 className="PH-header-title">The iSpeakWell Project</h1>
        <Spinner />
        <p>Loading your purchase history…</p>
          <GlobalNavDrawer
            hideCurrent
            links={[
              { to: "/purchase", label: `Tokens: ${balance}`},
              { to: "/voicebot", label: "iSpeakWell" },
              { to: "/resumerevisor", label: "Resumes" },
              { to: "/conversationpractice", label: "Conversations" },
              { to: "/interview-practice", label: "Interviews" },
              { to: "/termsofservice", label: "Terms" },
              { to: "/privacypolicy", label: "Privacy" },
              { to: "/admin", label: "Admin" },
              { to: "/login", label: "Logout", onClick: async () => { await logout(); } },
            ]}
          />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="PH-Menu-container">
        <h1 className="PH-header-title">The iSpeakWell Project</h1>
        <p>Please sign in to view your purchase history.</p>
      </div>
    );
  }

  const buildFullText = (r) => {
    const created = r.createdAt?.toDate ? r.createdAt.toDate() : null;
    const dateStr = created ? created.toLocaleString() : "—";
    return [
      `Receipt #${r.appReceiptNumber}`,
      dateStr,
      `Purchased: ${r.tokensPurchased}`,
      `Bonus ×${r.bonusMultiplier || 1}`,
      `Credited: ${r.tokensCredited}`,
      `Status: ${r.status || "Successful"}`,
      `Session ID: ${r.sessionId || "—"}`,
      `Previous Balance: ${r.prevTokens}`,
      `New Balance: ${r.newBalance}`,
      `Stripe URL: ${r.stripeReceiptUrl || "— (may expire)"}`
    ].join("\n");
  };

  return (
    <div className="PH-Menu-container">
      <h1 className="PH-header-title">The iSpeakWell Project</h1>
      <h2 className="PH-Task-Menu">Purchase History & Receipts</h2>
      <hr />

      <div className="PH-Layout">
        {/* Left: sticky/narrow panel for TokenHistory */}
        <aside className="PH-Aside">
          <TokenHistory title="Token History" />
        </aside>

        {/* Right: receipts as card grid */}
        <main className="PH-MainArea">
          {receipts.length === 0 ? (
            <p>No purchases found yet.</p>
          ) : (
            <div className="PH-CardGrid">
              {receipts.map((r) => {
                const created = r.createdAt?.toDate ? r.createdAt.toDate() : null;
                const dateStr = created ? created.toLocaleString() : "—";
                const isOpenCard = openId === r.id;
                const detailsId = `ph-details-${r.id}`;

                return (
                  <article
                    key={r.id}
                    className={`PH-Card ${isOpenCard ? "is-open" : ""}`}
                    data-open={isOpenCard ? "true" : "false"} // purely for styling if you want
                  >
                    <header className="PH-CardHeader" id={`ph-h-${r.id}`}>
                      <div className="PH-ReceiptLine">
                        <span className="PH-ReceiptNumber">Receipt #{r.appReceiptNumber}</span>
                        <span className={`PH-StatusBadge ${String(r.status || "Successful").toLowerCase()}`}>
                          {r.status || "Successful"}
                        </span>
                      </div>
                      <time className="PH-Date">{dateStr}</time>
                    </header>

                    <section className="PH-Core">
                      <div className="PH-Metrics">
                        <div className="PH-Metric">
                          <label>Purchased</label><strong>{r.tokensPurchased}</strong>
                        </div>
                        <div className="PH-Metric">
                          <label>Bonus</label><strong>×{r.bonusMultiplier || 1}</strong>
                        </div>
                        <div className="PH-Metric">
                          <label>Credited</label><strong>{r.tokensCredited}</strong>
                        </div>
                        <div className="PH-Metric">
                          <label>Prev</label><strong>{r.prevTokens}</strong>
                        </div>
                        <div className="PH-Metric">
                          <label>New</label><strong>{r.newBalance}</strong>
                        </div>
                      </div>

                      <div className="PH-QuickActions">
                        <button onClick={() => openStripeReceipt(r.stripeReceiptUrl)}>Open Stripe</button>
                        <button onClick={() => copyText(r.appReceiptNumber)}>Copy #</button>
                        <button onClick={() => copyText(buildFullText(r))}>Copy All</button>
                        <button
                          type="button"
                          className="PH-ExpandBtn"
                          aria-expanded={isOpenCard}         
                          aria-controls={detailsId}
                          onClick={() => setOpenId((cur) => (cur === r.id ? null : r.id))}
                        >
                          {isOpenCard ? "Hide details" : "Show details"}
                        </button>
                      </div>
                    </section>

                    {isOpenCard && (
                      <section
                        id={detailsId}
                        className="PH-Details"
                        aria-label={`ph-h-${r.id}`}
                        hidden={!isOpenCard}
                      >
                        {/* Session ID */}
                        <div className="PH-Code PH-Code--single">
                          <pre>{r.sessionId || "—"}</pre>
                          {r.sessionId && (
                            <button className="PH-Copy" onClick={() => copyText(r.sessionId)}>Copy</button>
                          )}
                        </div>

                        {/* Stripe URL */}
                        <div className="PH-Code PH-Code--double">
                          <pre>{r.stripeReceiptUrl || "— (may expire)"}</pre>
                          {r.stripeReceiptUrl && (
                            <>
                              <button className="PH-Copy" onClick={() => copyText(r.stripeReceiptUrl)}>Copy</button>
                              <button className="PH-Open" onClick={() => openStripeReceipt(r.stripeReceiptUrl)}>Open</button>
                            </>
                          )}
                        </div>
                      </section>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </main>
      </div>
          <GlobalNavDrawer
              hideCurrent
              links={[
                { to: "/purchase", label: `Tokens: ${balance}`},
                { to: "/admin", label: "Admin" },
                { to: "/resumerevisor", label: "Resume Revisor" },
                { to: "/conversationpractice", label: "Conversation Practice" },
                { to: "/interview-practice", label: "Interview Practice" },
                { to: "/termsofservice", label: "Terms" },
                { to: "/privacypolicy", label: "Privacy Policy" },
                { to: "/login", label: "Logout", onClick: async () => { await logout(); } },
              ]}
              />      
      </div>
  );
}
